import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  FormRow,
  Icon,
  IconButton,
  Input,
  Modal,
  Radio,
  Stack,
  Text,
  Tooltip
} from '@efast_public/fjd-component-library';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../utils/joiConfig';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GovosApiRoutes, govosClient } from '../api/govos-api';
import { useNotification } from '../shared/notification/useNotification';
import { useState } from 'react';

interface AddMemberModalProps {
  onClose?: () => void;
  onSuccess?: () => void;
  isOpen: boolean;
  startAppId: number;
}

type AddMemberFormData = {
  email: string;
  role: string;
};

const getValidationSchema = (
  CustomJoi: ReturnType<typeof createJoiWithTranslations>
) =>
  CustomJoi.object<AddMemberFormData>({
    email: CustomJoi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        'string.email': 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
        'any.required': 'Bitte geben Sie eine E-Mail-Adresse ein'
      }),
    role: CustomJoi.string().required().messages({
      'any.required': 'Bitte wählen Sie eine Rolle aus'
    })
  });

export function AddMemberModal({
  onClose,
  onSuccess,
  isOpen,
  startAppId
}: AddMemberModalProps) {
  const { t } = useTranslation();
  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();
  const CustomJoi = createJoiWithTranslations(t);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const { register, handleSubmit, formState, control, reset, setError, watch } =
    useForm<AddMemberFormData>({
      defaultValues: {
        email: '',
        role: 'member'
      },
      resolver: joiResolver(getValidationSchema(CustomJoi)),
      mode: 'onBlur'
    });

  const { mutate: addMember, isPending } = useMutation({
    mutationFn: async (data: AddMemberFormData) => {
      const roleValue = data.role === 'member' ? 4 : 5;
      const result = await govosClient.post(
        GovosApiRoutes.addMember(startAppId),
        {
          email: data.email,
          role: roleValue
        }
      );

      // Check if the response contains an error in the body
      if (result.data && result.data.result === 'error') {
        const error = result.data.error as { msg?: string; code?: number };
        throw new Error(
          error.msg || 'Mitglied konnte nicht hinzugefügt werden'
        );
      }

      return result;
    },
    onError: (error: unknown) => {
      let message: string;

      // Check if it's a plain Error (thrown manually)
      if (error instanceof Error) {
        message = error.message;
      } else {
        // Handle axios errors
        const axiosError = error as {
          response?: {
            data?: {
              error?: { msg?: string; code?: number };
              message?: string;
            };
            status?: number;
          };
        };
        message =
          axiosError.response?.data?.error?.msg ??
          axiosError.response?.data?.message ??
          '';
      }

      setErrorMessage(message);

      if (message.includes('existiert nicht') || message.includes('404')) {
        setEmailError(
          'Bitte prüfen Sie, ob die E-Mail Adresse korrekt eingegeben wurde. Die E-Mail Adresse muss zu einem schon existierendem Benutzerkonto auf dem System gehören.'
        );
        setError('email', {
          type: 'manual',
          message:
            'Bitte prüfen Sie, ob die E-Mail Adresse korrekt eingegeben wurde. Die E-Mail Adresse muss zu einem schon existierendem Benutzerkonto auf dem System gehören.'
        });
      }
    },
    onSuccess: (result) => {
      notifyUserFromRequest(async () => result, {
        successMessage: t('api.success.addMember')
      });
      queryClient.invalidateQueries({
        queryKey: [GovosApiRoutes.members(startAppId)]
      });
      reset();
      setErrorMessage(null);
      setEmailError(null);
      onSuccess?.();
    }
  });

  const handleSave = handleSubmit((data) => {
    setErrorMessage(null);
    setEmailError(null);
    addMember(data);
  });

  const handleClose = () => {
    setErrorMessage(null);
    setEmailError(null);
    onClose?.();
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('AddMemberModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <Stack spacing="l">
          {errorMessage && (
            <Alert
              variant="error"
              message={`${t('api.error.addMember')}${errorMessage ? ` ${errorMessage}` : ''}`}
              onClose={() => setErrorMessage(null)}
            />
          )}
          <FormRow
            label={
              <div>
                <div>
                  <Text strong>{t('AddMemberModal.emailLabel')}</Text>
                </div>
                <Text size="s">{t('AddMemberModal.emailDescription')}</Text>
              </div>
            }
            validationMessage={formState.errors.email?.message || emailError}
            variant={formState.errors.email || emailError ? 'error' : undefined}
          >
            <Input
              {...register('email')}
              placeholder={t('AddMemberModal.emailPlaceholder')}
              onChange={(e) => {
                void register('email').onChange(e);
                setEmailError(null);
                setErrorMessage(null);
              }}
            />
          </FormRow>

          <FormRow
            label={
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Text strong>{t('AddMemberModal.roleLabel')}</Text>
                <Tooltip
                  tooltip={t(
                    'ZustaendigeStelle.memberManagement.roleDescriptions'
                  )}
                >
                  <IconButton
                    variant="primary-text"
                    label=""
                    icon="information-filled"
                    size="sm"
                  />
                </Tooltip>
              </div>
            }
            validationMessage={formState.errors.role?.message}
            variant={formState.errors.role ? 'error' : undefined}
          >
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <Stack spacing="s" orientation="vertical">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Icon name="user" size="sm" />
                    <Radio
                      name="role"
                      value="member"
                      checked={field.value === 'member'}
                      onChange={() => field.onChange('member')}
                      label={t(
                        'ZustaendigeStelle.memberManagement.role.member'
                      )}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Icon name="user-role" size="sm" />
                    <Radio
                      name="role"
                      value="moderator"
                      checked={field.value === 'moderator'}
                      onChange={() => field.onChange('moderator')}
                      label={t(
                        'ZustaendigeStelle.memberManagement.role.moderator'
                      )}
                    />
                  </div>
                </Stack>
              )}
            />
          </FormRow>
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%'
          }}
        >
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={!watch('email') || !!formState.errors.email || isPending}
            style={{ width: '100%' }}
          >
            {isPending
              ? t('AddMemberModal.actions.adding')
              : t('AddMemberModal.actions.add')}
          </Button>
          <Button
            variant="secondary-outline"
            onClick={handleClose}
            style={{ width: '100%' }}
          >
            {t('AddMemberModal.actions.cancel')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
